import { useEffect, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import type { AuthSession } from '../../types/plans'
import { staffApi } from '../../api/staff'
import type { StaffPatientDiagnocatData } from '../../types/plans'

export function PatientDiagnocatSection({
  bootstrap,
  session,
  patientId,
  enabled,
}: {
  bootstrap: TenantBootstrapModel
  session: AuthSession
  patientId: number | null
  enabled: boolean
}) {
  const [data, setData] = useState<StaffPatientDiagnocatData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [openingUid, setOpeningUid] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || !patientId) {
      setData(null)
      setError(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    void staffApi
      .getPatientDiagnocat(bootstrap, session, patientId)
      .then((payload) => {
        if (!cancelled) setData(payload)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setData(null)
          setError(err instanceof Error ? err.message : 'Не удалось загрузить Diagnocat')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [bootstrap, session, patientId, enabled])

  if (!enabled || !patientId) {
    return null
  }

  if (loading) {
    return (
      <section className="grid gap-3 rounded-[28px] border border-slate-200/70 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">Diagnocat</h3>
        <p className="text-sm text-slate-500">Загружаем данные Diagnocat...</p>
      </section>
    )
  }

  if (error) {
    return (
      <section className="grid gap-3 rounded-[28px] border border-slate-200/70 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900">Diagnocat</h3>
        <p className="text-sm text-slate-500">{error}</p>
      </section>
    )
  }

  if (!data?.linked) {
    return null
  }

  const openReport = async (analysisUid: string) => {
    setOpeningUid(analysisUid)
    try {
      const pdf = await staffApi.getPatientDiagnocatReportPdf(bootstrap, session, patientId, analysisUid)
      if (pdf.pdf_url) {
        window.open(pdf.pdf_url, '_blank', 'noopener,noreferrer')
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Не удалось открыть отчёт')
    } finally {
      setOpeningUid(null)
    }
  }

  return (
    <section className="grid gap-4 rounded-[28px] border border-emerald-200/80 bg-emerald-50/40 p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Diagnocat</h3>
          <p className="mt-1 text-sm text-slate-600">
            Связка подтверждена · исследований: {data.total_studies} · анализов: {data.total_analyses}
          </p>
        </div>
        <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-emerald-800">
          linked
        </span>
      </div>

      <div className="grid gap-3">
        {data.studies.map((study) => (
          <article key={study.study_uid} className="grid gap-3 rounded-[20px] border border-white/80 bg-white px-4 py-4">
            <div>
              <strong className="text-slate-900">{study.study_name ?? study.study_type ?? 'Исследование'}</strong>
              {study.study_date ? (
                <p className="mt-1 text-sm text-slate-500">{new Date(study.study_date).toLocaleString('ru-RU')}</p>
              ) : null}
            </div>
            {study.analyses.length > 0 ? (
              <div className="grid gap-2">
                {study.analyses.map((analysis) => (
                  <div key={analysis.analysis_uid} className="flex flex-wrap items-center justify-between gap-2 rounded-[14px] bg-slate-50 px-3 py-2">
                    <div>
                      <span className="text-sm font-medium text-slate-900">{analysis.title ?? analysis.analysis_type ?? 'Анализ'}</span>
                      {analysis.status ? <small className="ml-2 text-xs uppercase text-slate-500">{analysis.status}</small> : null}
                    </div>
                    {analysis.has_pdf ? (
                      <button
                        type="button"
                        className="rounded-[12px] border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-800 hover:bg-sky-100 disabled:opacity-60"
                        disabled={openingUid === analysis.analysis_uid}
                        onClick={() => void openReport(analysis.analysis_uid)}
                      >
                        {openingUid === analysis.analysis_uid ? 'Открываем...' : 'Открыть отчёт'}
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Анализы пока не синхронизированы.</p>
            )}
          </article>
        ))}
      </div>
    </section>
  )
}
